import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../../services/api';
import Hls from 'hls.js';
import HotspotSelector from '../HotspotSelector/HotspotSelector';

const VideoPlayer = () => {
  const { city } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const hasNavigatedRef = useRef(false);
  const hlsRef = useRef(null);
  const [playbackSource, setPlaybackSource] = useState({ type: 'hls', url: '' });
  const [error, setError] = useState(null);
  const [isPreparingHotspots, setIsPreparingHotspots] = useState(false);
  const [prepMessage, setPrepMessage] = useState('');
  const [cityData, setCityData] = useState(null);
  const [showHotspots, setShowHotspots] = useState(false);
  const hasTransitionedRef = useRef(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  // Ensure back navigates to city selector with preserved selection
  useEffect(() => {
    if (!city) return undefined;
    const sanitizedCity = city.toLowerCase().replace(/'/g, '').trim();
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('selectedCity', sanitizedCity);
      }
    } catch (_) {}

    if (typeof window === 'undefined') return undefined;
    try { window.history.pushState({ videoGuard: true }, ''); } catch (_) {}
    const handlePop = () => {
      try { sessionStorage.setItem('selectedCity', sanitizedCity); } catch (_) {}
      navigate('/city-selection', { state: { selectedCity: sanitizedCity } });
    };
    window.addEventListener('popstate', handlePop);
    return () => {
      window.removeEventListener('popstate', handlePop);
    };
  }, [city, navigate]);

  useEffect(() => {
    const fetchAndSelectSource = async () => {
      if (!city) {
        setError('No city specified');
        return;
      }

      try {
        const sanitizedCity = city.toLowerCase().replace(/'/g, '').trim();
        const hlsUrl = `/videos/${sanitizedCity}/master.m3u8`;
        setPlaybackSource({ type: 'hls', url: hlsUrl });
        setIsVideoLoading(true);
        // Prefetch city data for seamless hotspot render
        try {
          const data = await ApiService.getCityData(sanitizedCity);
          setCityData(data);
        } catch (_) {}
      } catch (err) {
        console.error('Error preparing video sources:', err);
        setError(`Failed to load video for ${city}`);
      }
    };

    fetchAndSelectSource();
  }, [city]);

  // Attach HLS or use native/Safari HLS; show error if unsupported
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Cleanup previous HLS instance if any
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch (_) {}
      hlsRef.current = null;
    }

    if (playbackSource.type === 'hls') {
      if (Hls.isSupported()) {
        const hls = new Hls({ startLevel: 1, capLevelToPlayerSize: true, maxInitialBitrate: 2500000 });
        hlsRef.current = hls;
        hls.loadSource(playbackSource.url);
        hls.attachMedia(video);
        const onManifestParsed = () => {
          // remain loading until the first frame starts playing
          video.play().catch((e) => console.log('Auto-play failed (HLS):', e));
        };
        const onError = (_event, data) => {
          if (data && data.fatal) {
            try { hls.destroy(); } catch (_) {}
            hlsRef.current = null;
            setError('HLS playback failed. Please try again.');
            setIsVideoLoading(false);
          }
        };
        hls.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);
        hls.on(Hls.Events.ERROR, onError);

        return () => {
          hls.off(Hls.Events.MANIFEST_PARSED, onManifestParsed);
          hls.off(Hls.Events.ERROR, onError);
          try { hls.destroy(); } catch (_) {}
          hlsRef.current = null;
        };
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS
        const onLoadedMetadata = () => {
          video.play().catch((e) => console.log('Auto-play failed (Safari HLS):', e));
        };
        const onError = () => {
          setError('HLS playback failed. Please try again.');
          setIsVideoLoading(false);
        };
        video.src = playbackSource.url;
        video.addEventListener('loadedmetadata', onLoadedMetadata);
        video.addEventListener('error', onError);
        return () => {
          video.removeEventListener('loadedmetadata', onLoadedMetadata);
          video.removeEventListener('error', onError);
          try { video.removeAttribute('src'); video.load(); } catch (_) {}
        };
      } else {
        // No HLS support
        setError('HLS not supported in this browser.');
        setIsVideoLoading(false);
      }
    }
  }, [playbackSource]);

  // Handle video element errors for native HLS
  const handleVideoElementError = () => {
    setError('HLS playback failed. Please try again.');
    setIsVideoLoading(false);
  };

  const handleVideoWaiting = () => {
    setIsVideoLoading(true);
  };

  const handleVideoPlaying = () => {
    setIsVideoLoading(false);
  };

 

  const triggerCrossfadeToHotspots = () => {
    if (hasTransitionedRef.current) return;
    hasTransitionedRef.current = true;
    setShowHotspots(true);
  };

  const handleVideoEnd = () => {
    // Cleanup HLS after playback ends
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch (_) {}
      hlsRef.current = null;
    }
  };

  const handleVideoClick = () => {
    triggerCrossfadeToHotspots();
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    if (videoRef.current.currentTime >= 7 && !hasTransitionedRef.current) {
      triggerCrossfadeToHotspots();
    }
  };

  // No full-screen loader: keep page content, show overlay badge instead

  // Error banner overlay instead of page swap

  return (
    <div className="fixed inset-0 w-screen h-screen bg-black overflow-hidden">
      {/* Hotspots mounted underneath from the start for preloading */}
      <div className="absolute inset-0 z-0" style={{ visibility: showHotspots ? 'visible' : 'hidden' }}>
        <HotspotSelector embedded={true} initialCityData={cityData} />
      </div>
      {isPreparingHotspots && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <div className="px-4 py-2 rounded-lg bg-black/40 text-white text-sm backdrop-blur-sm">
            {prepMessage || 'Loading hotspots...'}
          </div>
        </div>
      )}
      {/* Video loading indicator */}
      {!showHotspots && isVideoLoading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            <span className="text-white/80 text-sm">Loading video…</span>
          </div>
        </div>
      )}
      {error && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40">
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-red-600/80 text-white shadow-lg">
            <span>Error: {error}</span>
            <button
              onClick={() => navigate('/city-selection')}
              className="bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 rounded"
            >
              Back
            </button>
          </div>
        </div>
      )}
      <div
        className="absolute inset-0 z-10 transition-opacity duration-700"
        style={{ opacity: showHotspots ? 0 : 1, pointerEvents: showHotspots ? 'none' : 'auto' }}
      >
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          onEnded={handleVideoEnd}
          onTimeUpdate={handleTimeUpdate}
          onError={handleVideoElementError}
          onWaiting={handleVideoWaiting}
          onPlaying={handleVideoPlaying}
          muted
          playsInline
          style={{
            margin: 0,
            padding: 0,
            border: 'none',
            outline: 'none'
          }}
        >
          Your browser does not support the video tag.
        </video>
      </div>
      {/* Skip indicator: visible only while video layer is active */}
      {!showHotspots && (
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={handleVideoClick}
            className="bg-black/50 text-white px-4 py-2 rounded-lg backdrop-blur-sm hover:bg-black/70 transition-colors"
          >
            Skip →
          </button>
        </div>
      )}
      {/* City indicator */}
      <div className="absolute bottom-4 left-4 z-20">
        <div className="bg-black/50 text-white px-4 py-2 rounded-lg backdrop-blur-sm">
          <span className="text-sm uppercase tracking-wider">
            {city} Experience
          </span>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;